/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import moment from 'moment';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import _ from 'lodash';

import { AddIcon, ChevronRightIcon, CloseIcon, DraftsIcon } from '@treams/treams-icons';
import {
	Avatar,
	Button,
	CardList,
	Paragraph,
	QuillText,
	SegmentedControl,
	Separator,
	Sidebar,
	Skeleton,
	Subheading,
	Tags,
} from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import GoalCard from '../goals/GoalCard';
import FeedbackResultQuestion from '../feedback/results/FeedbackResultQuestion';
import useLocalize from '../packages/kakapo-ui-application/hooks/useLocalize';
import useViewer from '../packages/kakapo-ui-application/hooks/useViewer';
import { useModalLink } from '../navigation/ModalNavigation';
import useFeature from '../packages/kakapo-ui-application/hooks/useFeature';
import NoteCard from 'notes/NoteCard';
import { Compliment } from '../generated/graphql';

const SidebarContainer = styled(Box)`
	height: 100%;
	overflow: auto;
`;

const Rotation = styled(Box)<{ rotate: string }>`
	transform: rotate(${(props) => props.rotate});
	transition: transform 200ms ease-out;
`;

function SidebarDismissHeader(
	props: PropsWithChildren<{ onClick?: () => void; isOpen?: boolean }>,
) {
	return (
		<Box
			onClick={() => props.onClick?.()}
			style={{ cursor: props.onClick ? 'pointer' : 'default' }}
		>
			<Flex alignItems="center" py={3}>
				<Subheading mr={2}>{props.children}</Subheading>
				<Box flexGrow={1} flexShrink={1} />
				<Box width={24}>
					<CloseIcon />
				</Box>
			</Flex>
		</Box>
	);
}

const SidebarHeaderAccessory = styled(Box)`
	position: absolute;
	top: 12px;
	right: 0;
`;

function SidebarHeader(
	props: PropsWithChildren<{ onClick?: () => void; isOpen?: boolean; createLink?: string }>,
) {
	return (
		<Box style={{ position: 'relative' }}>
			{props.createLink && (
				<SidebarHeaderAccessory>
					<Button type="transparent" small icon={<AddIcon />} link={props.createLink} />
				</SidebarHeaderAccessory>
			)}
			<Box
				onClick={() => props.onClick?.()}
				style={{ cursor: props.onClick ? 'pointer' : 'default' }}
			>
				<Separator mx={-3} />
				<Flex alignItems="center" my={3}>
					{props.isOpen !== undefined && (
						<Rotation width={24} mr={2} rotate={props.isOpen ? '90deg' : '0deg'}>
							<ChevronRightIcon />
						</Rotation>
					)}
					<Subheading width="100%">
						<Flex width="100%" justifyContent="space-between" alignItems="center">
							{props.children}
						</Flex>
					</Subheading>
				</Flex>
			</Box>
		</Box>
	);
}

function JobPosition(props: { position: graphql.JobPosition }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	return (
		<Sidebar.Item
			path={useModalLink(`/job-positions/${props.position.id}`)}
			title={
				props.position.jobLevel
					? `${localize(props.position.jobTitle.title).text} - ${
							localize(props.position.jobLevel.title).text
					  }`
					: localize(props.position.jobTitle.title).text
			}
			subtitle={`${moment(props.position.startDate).format('MMMM YYYY')} –
        ${
					props.position.endDate
						? moment(props.position.endDate).format('MMMM YYYY')
						: t('conversation.sidebar.jobs.current.title')
				}`}
			small
		/>
	);
}

function SidebarPeekJobs(props: { user: graphql.User }) {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);

	return (
		<>
			<SidebarHeader isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
				{t('conversation.sidebar.jobs.title')}
			</SidebarHeader>
			{isOpen && (
				<Box>
					{!props.user.jobPositions && (
						<Skeleton.Repeat count={3}>
							<Sidebar.Item.Skeleton />
						</Skeleton.Repeat>
					)}
					{props.user.jobPositions.map((position) => (
						<JobPosition key={position.id} position={position} />
					))}
				</Box>
			)}
		</>
	);
}

function FeedbackRequestResultsSmall(props: { requestID: string }) {
	const request = graphql.useFeedbackResultsQuery({ variables: { id: props.requestID } });

	const responses = _.sortBy(
		request.data?.feedbackRequest.responses.edges.map(({ node }) => node),
		(response) => `${response.recipient.firstName} ${response.recipient.lastName}`,
	);

	return (
		<>
			{!request.data && <FeedbackResultQuestion.Skeleton />}
			{request.data &&
				responses &&
				request.data?.feedbackRequest.questions.map((question) => (
					<Box mb={3} key={question.id}>
						<FeedbackResultQuestion
							request={request.data!.feedbackRequest as graphql.FeedbackRequest}
							question={question as graphql.FeedbackRequestQuestion}
							response={undefined}
							responses={responses as graphql.FeedbackResponse[]}
							small
						/>
					</Box>
				))}
		</>
	);
}

function SidebarPeekConversations(props: { userID: string; conversationID: string }) {
	const { t } = useTranslation();

	const { data } = graphql.useEvaluationSubmissionsQuery({
		variables: { userID: props.userID, last: 100, isArchived: false },
	});

	const [showAll, setShowAll] = useState(false);
	const [isOpen, setIsOpen] = useState(true);
	const conversations = _.orderBy(
		data?.user.evaluationSubmissions.edges ?? [],
		({ node }) => node.createdAt,
		'desc',
	)
		.map(({ node }) => node)
		.slice(0, showAll ? data?.user.evaluationSubmissions.edges.length : 4);

	return (
		<>
			<SidebarHeader isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
				{t('conversation.sidebar.previous.title')}
			</SidebarHeader>
			{isOpen && (
				<Box mb={3}>
					{!conversations && (
						<Skeleton.Repeat count={3}>
							<Sidebar.Item.Skeleton />
						</Skeleton.Repeat>
					)}
					{conversations?.map((conversation) => (
						<ConversationSideBarItem
							key={conversation.id}
							userID={props.userID}
							conversation={conversation as graphql.EvaluationSubmission}
						/>
					))}
					{!showAll && (
						<Box mx="10px" my={2}>
							<Paragraph>
								<div onClick={() => setShowAll(true)}>
									{t('conversation.sidebar.previous.showAll.title')}
								</div>
							</Paragraph>
						</Box>
					)}
				</Box>
			)}
		</>
	);
}

type ConversationSideBarItemProps = {
	conversation: graphql.EvaluationSubmission;
	userID: string;
};

const ConversationSideBarItem = ({ conversation, userID }: ConversationSideBarItemProps) => {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const viewer = useViewer();

	const date = moment(conversation.deadline).format('DD/MM/YYYY');
	const time = moment(conversation.deadline).format('HH:MM');

	const conversationsPath =
		viewer?.id === userID ? `/conversations` : `/users/${userID}/conversations`;

	return (
		<Sidebar.Item
			key={conversation.id}
			title={localize(conversation.evaluationForm.title).text}
			subtitle={t('conversation.sidebar.item.dueDateWithTime', { date, time })}
			path={`${conversationsPath}/${conversation.id}`}
			small
		/>
	);
};

const ComplimentDateLabel = styled(Paragraph)`
	opacity: 0;
	transition: 250ms opacity ease-out;
	font-size: 12px;
`;

const ComplimentListItemContainer = styled(Flex)`
	page-break-inside: avoid;
	margin-bottom: 10px;

	:hover {
		${ComplimentDateLabel} {
			opacity: 1;
		}
	}
`;

function ComplimentListItem(props: { compliment: Compliment }) {
	return (
		<ComplimentListItemContainer alignItems="left" justifyContent="left">
			<Avatar
				user={props.compliment.sender}
				size={32}
				flexGrow={0}
				flexShrink={0}
				marginRight="10px"
			/>

			<Flex alignItems="left" flexDirection="column" flexGrow={1}>
				<Flex justifyContent="space-between" alignItems="center">
					<Subheading>
						{props.compliment.sender.firstName} {props.compliment.sender.lastName}
					</Subheading>
					<ComplimentDateLabel>
						{moment(props.compliment.createdAt).format('LL')}
					</ComplimentDateLabel>
				</Flex>
				{props.compliment.text && (
					<Paragraph mt={2} marginTop={0}>
						<QuillText value={props.compliment.text} />
					</Paragraph>
				)}
			</Flex>
		</ComplimentListItemContainer>
	);
}

function SidebarPeekCompliment(props: { userID: string }) {
	const { t } = useTranslation();
	const userCompliments = graphql.useUserComplimentsQuery({
		variables: { id: props.userID },
	});

	const compliments = userCompliments.data?.user.compliments.edges.slice().reverse() || [];

	const [isOpen, setIsOpen] = useState(false);

	return (
		<>
			<SidebarHeader isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
				{t('conversation.sidebar.compliments.title')}
			</SidebarHeader>
			{isOpen &&
				(compliments.length ? (
					compliments?.map(({ node }) => (
						<ComplimentListItem key={node.id} compliment={node as graphql.Compliment} />
					))
				) : (
					<Paragraph marginBottom={10}>{t('conversation.sidebar.compliments.empty')}</Paragraph>
				))}
		</>
	);
}

function SidebarPeekFeedback(props: { userID: string }) {
	const { t } = useTranslation();
	const feedback = graphql.useFeedbackRequestsQuery({
		variables: { userID: props.userID, last: 10, isArchived: false },
	});

	const options = _.orderBy(
		feedback.data?.user.feedbackRequests.edges ?? [],
		({ node }) => node.createdAt,
		'desc',
	).map(({ node }) => ({
		title: node.title || t('feedback.incoming.card.title'),
		value: node.id,
	}));

	const [isOpen, setIsOpen] = useState(false);
	const [selectedFeedback, setSelectedFeedback] = useState<undefined | string>(undefined);

	useEffect(() => {
		if (!options || options.length < 1) {
			return;
		}

		if (selectedFeedback === undefined) {
			setSelectedFeedback(options[0]!.value);
		}
	}, [options]);

	return (
		<>
			<SidebarHeader isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
				{t('conversation.sidebar.feedback.title')}
			</SidebarHeader>

			{isOpen && (
				<>
					{options && (
						<Box mb={3}>
							<Tags.Item.Select
								label={t('conversation.sidebar.feedback.request.label')}
								options={options}
								selectedValue={selectedFeedback!}
								onChange={setSelectedFeedback}
							/>
						</Box>
					)}

					{selectedFeedback && <FeedbackRequestResultsSmall requestID={selectedFeedback} />}
				</>
			)}
		</>
	);
}

function CompactSharedNotesList(props: { userID: string; otherID: string }) {
	const location = useLocation();

	const query = graphql.useMutualNotesQuery({
		variables: {
			first: 5, // I'm not sure if this is doing anything since the mutualNotes query doesn't use this variable
			id: props.otherID,
			filters: { showArchived: false },
		},
	});

	useEffect(() => {
		query.refetch();
	}, [location]);

	return (
		<CardList
			translationName="notes.index"
			query={query}
			path="user.mutualNotes"
			icon={<DraftsIcon />}
			renderCard={(item: graphql.Note) => (
				<NoteCard userID={props.userID} note={item} compact modal />
			)}
			renderSkeleton={() => <NoteCard.Skeleton compact />}
			compact
		/>
	);
}

function CompactPrivateNotesList(props: { userID: string }) {
	const location = useLocation();

	const query = graphql.useNotesQuery({
		variables: {
			first: 5,
			filters: {
				isArchived: false,
			},
		},
	});

	useEffect(() => {
		query.refetch();
	}, [location]);

	return (
		<CardList
			translationName="notes.index"
			query={query}
			path="notes"
			icon={<DraftsIcon />}
			renderCard={(item: graphql.Note) => (
				<NoteCard userID={props.userID} note={item} compact modal />
			)}
			renderSkeleton={() => <NoteCard.Skeleton compact />}
			compact
		/>
	);
}

function SidebarPeekNotes(props: {
	userID: string;
	conversation: graphql.EvaluationSubmission;
	viewer: graphql.User;
}) {
	const { t } = useTranslation();

	const [isOpen, setIsOpen] = useState(false);

	const [tab, setTab] = useState<'shared' | 'private'>('shared');

	const isEmployee = props.viewer.id === props.conversation.employee.user.id;
	const otherID = isEmployee
		? props.conversation.manager.user.id
		: props.conversation.employee.user.id;

	const link = useModalLink(tab === 'shared' ? `/notes/new/${otherID}` : '/notes/new');

	return (
		<>
			<SidebarHeader isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} createLink={link}>
				{t('conversation.sidebar.notes.title')}
			</SidebarHeader>

			{isOpen && (
				<Box mb={3}>
					<Flex mb={2}>
						<SegmentedControl
							options={[
								{ title: t('conversation.peek.notes.shared'), value: 'shared' },
								{ title: t('conversation.peek.notes.private'), value: 'private' },
							]}
							value={tab}
							onChange={(value) => setTab(value as 'shared' | 'private')}
						/>
					</Flex>

					{tab === 'shared' && <CompactSharedNotesList userID={props.userID} otherID={otherID} />}
					{tab === 'private' && <CompactPrivateNotesList userID={props.userID} />}
				</Box>
			)}
		</>
	);
}

function CompactGoalsList(props: { userID: string; status: graphql.GoalStatus }) {
	const location = useLocation();

	// TODO: Add pagination?
	const goals = graphql.useIndividualGoalsQuery({
		variables: {
			id: props.userID,
			status: props.status,
			last: 10,
		},
	});

	useEffect(() => {
		goals.refetch();
	}, [location]);

	const sortedGoals = _.sortBy(goals.data?.user.goalsV2.edges ?? [], ({ node }) => -node.order).map(
		({ node }) => node,
	);

	return (
		<>
			{!goals.data && (
				<Skeleton.Repeat count={2}>
					<Box mb={2}>
						<GoalCard.Skeleton small />
					</Box>
				</Skeleton.Repeat>
			)}
			{sortedGoals.map((goal) => (
				<Box key={goal.id} mb={2}>
					<GoalCard modal goal={goal as unknown as graphql.Goal} small owner={false} />
				</Box>
			))}
		</>
	);
}

function SidebarPeekGoals(props: { userID: string; viewer: graphql.User }) {
	const { t } = useTranslation();

	const [isOpen, setIsOpen] = useState(false);

	const link = useModalLink(
		props.viewer.id === props.userID ? '/v2/goals/new' : `/v2/users/${props.userID}/goals/new`,
		true,
	);

	const [tab, setTab] = useState<'active' | 'completed'>('active');

	return (
		<>
			<SidebarHeader isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} createLink={link}>
				{t('conversation.sidebar.goals.title')}
			</SidebarHeader>

			{isOpen && (
				<>
					<Flex mb={2}>
						<SegmentedControl
							options={[
								{ title: t('conversation.peek.goals.active'), value: 'active' },
								{ title: t('conversation.peek.goals.completed'), value: 'completed' },
							]}
							value={tab}
							onChange={(value) => setTab(value as 'active' | 'completed')}
						/>
					</Flex>

					{tab === 'active' && (
						<CompactGoalsList userID={props.userID} status={graphql.GoalStatus.Active} />
					)}
					{tab === 'completed' && (
						<CompactGoalsList userID={props.userID} status={graphql.GoalStatus.Completed} />
					)}
				</>
			)}
		</>
	);
}

const ConversationSidebar = Skeleton(
	function ConversationSidebar(props: {
		onClose?: () => void;
		user: graphql.User;
		conversationID: string;
	}) {
		const { t } = useTranslation();

		const useFeedback = useFeature('2021.feedback');
		const useGoals = useFeature('2021.goals');
		const useNotes = useFeature('2021.notes');
		const useCompliments = useFeature('2021.compliments');
		const viewer = useViewer();

		const query = graphql.useEvaluationSubmissionQuery({ variables: { id: props.conversationID } });

		return (
			<SidebarContainer>
				<Box px={3} pb={3}>
					<SidebarDismissHeader onClick={() => props.onClose?.()}>
						{t('conversation.sidebar.context.title', {
							name: props.user.firstName,
						})}
					</SidebarDismissHeader>
					<SidebarPeekConversations conversationID={props.conversationID} userID={props.user.id} />
					{useGoals && props.user.viewerCanViewGoals && viewer && (
						<SidebarPeekGoals userID={props.user.id} viewer={viewer} />
					)}
					{useFeedback && props.user.viewerCanViewFeedback && (
						<SidebarPeekFeedback userID={props.user.id} />
					)}
					{useCompliments && props.user.viewerCanViewFeedback && (
						<SidebarPeekCompliment userID={props.user.id} />
					)}
					{useNotes && viewer && query.data && (
						<SidebarPeekNotes
							userID={props.user.id}
							viewer={viewer}
							conversation={query.data.evaluationSubmission as any}
						/>
					)}
					<SidebarPeekJobs user={props.user} />
				</Box>
			</SidebarContainer>
		);
	},
	function ConversationSidebarSkeleton() {
		return <SidebarContainer />;
	},
);

export default ConversationSidebar;
